import {
    accountingMigrationSchema,
    createAccountingMigrationBodySchema,
    createImportedAccountsFromBalanceSheetBodySchema,
    createImportedAccountsFromCoaBodySchema,
    coaDocumentMigrationSourceSchema,
    documentMigrationUploadBodySchema,
    documentMigrationUploadErrorSchema,
    documentMigrationUploadResponseSchema,
    extractMigrationFileBodySchema,
    extractMigrationFileResponseSchema,
    finalizeAccountsMappingBodySchema,
    getAccountsResponseSchema,
    qbdJsonDocumentMigrationBodySchema,
    qbdJsonDocumentMigrationResponseSchema,
    refreshAccountingMigrationBodySchema,
    setQbdFileMigrationUploadSourceBodySchema,
    qbdFileMigrationUploadSourceSchema,
    setGlExtractionBasisBodySchema,
    updateMigrationStartDateBodySchema,
    updateMigrationEndDateBodySchema,
    validateDocumentMigrationResponseSchema,
} from "@common/schemas/accounting-integration.schema";
import {
    bulkUpdateImportedAccountsBodySchema,
    importedAccountSchema,
} from "@common/schemas/domains/accounting/importedAccount.schema";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const entityPathParams = z.object({
    workspaceId: z.string().uuid(),
    entityId: z.coerce.number(),
});

export const accountingIntegrationContract = c.router(
    {
        createAccountingMigration: {
            method: "POST",
            path: "/:workspaceId/:entityId",
            pathParams: entityPathParams,
            body: createAccountingMigrationBodySchema,
            responses: {
                201: z.object({
                    accountingMigration: accountingMigrationSchema,
                }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        listWorkspaceAccountingMigrations: {
            method: "GET",
            path: "/migrations/:workspaceId",
            pathParams: z.object({ workspaceId: z.string().uuid() }),
            responses: {
                200: z.object({
                    accountingMigrations: z.array(accountingMigrationSchema),
                }),
                404: ErrorMessageSchema,
            },
        },
        getAccountingMigration: {
            method: "GET",
            path: "/:workspaceId/:entityId",
            pathParams: entityPathParams,
            responses: {
                200: z.object({
                    accountingMigration: accountingMigrationSchema,
                }),
                404: ErrorMessageSchema,
            },
        },
        removeAccountingMigration: {
            method: "DELETE",
            path: "/:workspaceId/:entityId",
            pathParams: entityPathParams,
            body: z.object({}),
            responses: {
                200: z.object({}),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateMigrationStartDate: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/start-date",
            pathParams: entityPathParams,
            body: updateMigrationStartDateBodySchema,
            responses: {
                200: z.object({
                    accountingMigration: accountingMigrationSchema,
                }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateMigrationEndDate: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/end-date",
            pathParams: entityPathParams,
            body: updateMigrationEndDateBodySchema,
            responses: {
                200: z.object({
                    accountingMigration: accountingMigrationSchema,
                }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        refreshAccountingMigration: {
            method: "POST",
            path: "/:workspaceId/:entityId/refresh",
            pathParams: entityPathParams,
            body: refreshAccountingMigrationBodySchema,
            responses: {
                200: z.object({}),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getAccounts: {
            method: "GET",
            path: "/:workspaceId/:entityId/accounts",
            pathParams: entityPathParams,
            query: z.object({
                requiresFinancialAccountMapping: z
                    .enum(["true", "false"])
                    .transform((v) => v === "true")
                    .optional(),
                orderBy: z.enum(["name", "institution"]).optional(),
            }),
            responses: {
                200: getAccountsResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkUpdateImportedAccounts: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/accounts",
            pathParams: entityPathParams,
            body: bulkUpdateImportedAccountsBodySchema,
            responses: {
                200: z.object({
                    accounts: z.array(importedAccountSchema),
                }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        finalizeAccountsMapping: {
            method: "POST",
            path: "/:workspaceId/:entityId/accounts/finalize",
            pathParams: entityPathParams,
            body: finalizeAccountsMappingBodySchema,
            responses: {
                200: z.object({}),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createImportedAccountsFromBalanceSheet: {
            method: "POST",
            path: "/:workspaceId/:entityId/accounts/from-balance-sheet",
            pathParams: entityPathParams,
            body: createImportedAccountsFromBalanceSheetBodySchema,
            responses: {
                200: z.object({
                    accountingMigration: accountingMigrationSchema,
                }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        createImportedAccountsFromCoa: {
            method: "POST",
            path: "/:workspaceId/:entityId/accounts/from-coa",
            pathParams: entityPathParams,
            body: createImportedAccountsFromCoaBodySchema,
            responses: {
                200: z.object({
                    accountingMigration: accountingMigrationSchema,
                }),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        finalizeMigratedDataMapping: {
            method: "POST",
            path: "/:workspaceId/:entityId/migrated-data/finalize",
            pathParams: entityPathParams,
            body: z.object({}),
            responses: {
                200: z.object({}),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        uploadDocumentMigration: {
            method: "POST",
            path: "/:workspaceId/:entityId/document-migration/:source",
            pathParams: entityPathParams.extend({
                source: coaDocumentMigrationSourceSchema,
            }),
            body: documentMigrationUploadBodySchema,
            responses: {
                200: documentMigrationUploadResponseSchema,
                400: documentMigrationUploadErrorSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        validateDocumentMigration: {
            method: "POST",
            path: "/:workspaceId/:entityId/document-migration/:source/validate",
            pathParams: entityPathParams.extend({
                source: coaDocumentMigrationSourceSchema,
            }),
            body: documentMigrationUploadBodySchema,
            responses: {
                200: validateDocumentMigrationResponseSchema,
                400: documentMigrationUploadErrorSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        extractMigrationFile: {
            method: "POST",
            path: "/:workspaceId/:entityId/document-migration/:source/extract",
            pathParams: entityPathParams.extend({
                source: coaDocumentMigrationSourceSchema,
            }),
            body: extractMigrationFileBodySchema,
            responses: {
                200: extractMigrationFileResponseSchema,
                400: documentMigrationUploadErrorSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        setQbdFileMigrationUploadSource: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/qbd-file-migration-source",
            pathParams: entityPathParams,
            body: setQbdFileMigrationUploadSourceBodySchema,
            responses: {
                200: z.object({
                    source: qbdFileMigrationUploadSourceSchema,
                }),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        uploadQbdJsonDocumentMigration: {
            method: "POST",
            path: "/:workspaceId/:entityId/document-migration-json",
            pathParams: entityPathParams,
            body: qbdJsonDocumentMigrationBodySchema,
            responses: {
                200: qbdJsonDocumentMigrationResponseSchema,
                400: documentMigrationUploadErrorSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        setGlExtractionBasis: {
            method: "PATCH",
            path: "/:workspaceId/:entityId/document-migration/gl-basis",
            pathParams: entityPathParams,
            body: setGlExtractionBasisBodySchema,
            responses: {
                200: z.object({}),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/accounting-integration",
    },
);
